import React, { useEffect, useRef, useState } from 'react';
import { useChat } from '../../context/ChatContext';
import BotChat from './BotChat';
import { useBotChat } from '../../hooks/useBotChat';
import './FloatingChatWidget.css';

const FloatingChatWidget = () => {
  const {
    isOpen,
    isMinimized,
    conversationId,
    token,
    unreadCount,
    toggleChat,
    maximizeChat,
    closeChat,
    clearUnreadCount,
  } = useChat();

  const widgetRef = useRef(null);
  const apiUrl = process.env.REACT_APP_PETER_BOT_API_URL || 'http://localhost:5002';
  
  // Get conversation status for header display
  // Always call hook, but pass null values if not available (hook handles it)
  const botChatData = useBotChat(conversationId || null, token || null, apiUrl);
  const status = botChatData?.status || null;

  // Clear unread count when chat is opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      clearUnreadCount();
    }
  }, [isOpen, isMinimized, clearUnreadCount]);

  // Debug logging
  useEffect(() => {
    console.log('[FloatingChatWidget] State:', {
      isOpen,
      isMinimized,
      hasConversationId: !!conversationId,
      hasToken: !!token,
    });
  }, [isOpen, isMinimized, conversationId, token]);

  return (
    <div className="floating-chat-widget" ref={widgetRef}>
      {/* Chat Window - Only show if we have conversationId and token */}
      {isOpen && conversationId && token && (
        <div className={`floating-chat-window ${isMinimized ? 'minimized' : ''}`}>
          <div className="floating-chat-header">
            <div className="floating-chat-header-info">
              <h3>{process.env.REACT_APP_BOT_NAME || 'Peter Bot'}</h3>
              {status === 'resolved' && (
                <span className="floating-chat-status-badge resolved">Resolved</span>
              )}
              {unreadCount > 0 && !isMinimized && status !== 'resolved' && (
                <span className="floating-chat-badge">{unreadCount}</span>
              )}
            </div>
            <div className="floating-chat-controls">
              <button
                className="floating-chat-btn minimize"
                onClick={() => toggleChat()}
                aria-label={isMinimized ? 'Maximize chat' : 'Minimize chat'}
              >
                {isMinimized ? '□' : '−'}
              </button>
              <button
                className="floating-chat-btn close"
                onClick={closeChat}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </div>
          {!isMinimized && (
            <div className="floating-chat-content">
              <BotChat
                conversationId={conversationId}
                token={token}
                apiUrl={apiUrl}
                onClose={closeChat}
                hideHeader={true}
              />
            </div>
          )}
        </div>
      )}

      {/* Floating Button - Always show when chat window is not open */}
      {!isOpen && (
        <button
          className="floating-chat-button"
          onClick={() => {
            if (conversationId && token) {
              toggleChat();
            } else {
              // No conversation yet - could show a message or check for active conversations
              console.log('[FloatingChatWidget] No active conversation. Waiting for email link or conversation to be created.');
            }
          }}
          aria-label={conversationId && token ? "Open chat" : "No active conversations"}
          title={conversationId && token ? "Open chat" : "No active conversations. Check your email for a chat link."}
          style={!conversationId || !token ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {unreadCount > 0 && conversationId && token && (
            <span className="floating-chat-button-badge">{unreadCount}</span>
          )}
        </button>
      )}

      {/* Minimized state - show button when minimized */}
      {isOpen && isMinimized && (
        <button
          className="floating-chat-button"
          onClick={maximizeChat}
          aria-label="Maximize chat"
          title="Maximize chat"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {unreadCount > 0 && (
            <span className="floating-chat-button-badge">{unreadCount}</span>
          )}
        </button>
      )}
    </div>
  );
};

export default FloatingChatWidget;

